import { api } from '../api/client';
import { API_URL } from '../utils';

/**
 * Which server switches are ON in the deployment this client talks to:
 * `GET /api/docs/contract` carries a `features` block (keepr-api KPR-755) —
 * today `{ repeatingGroups: boolean }`, the REPEATING_GROUPS switch (KPR-675).
 * The contract is public, reads no database and answers live on every request,
 * so a switch turned on or off is seen on the next call.
 *
 * Each feature reads as `true`, `false` or `null`:
 *
 *   true   the server said it is on;
 *   false  the server said it is off (exactly `false`);
 *   null   UNKNOWN — an API from before KPR-755 (no `features` block, or the
 *          key absent), a value that is not a boolean, or no answer at all
 *          (the network, a 5xx, a slow reply). Never read as off.
 *
 * What a caller does with `null` is its own call. The card editor offers the
 * repeating group only on `true` (see `groupsOffered` in CardDefinitionForm):
 * the owner's ruling is that the type is never shown while the switch is off,
 * and an unknown answer cannot promise it is on. Nothing here throws.
 */
export const NO_FEATURES = Object.freeze({ repeatingGroups: null });

const asFlag = value => (value === true || value === false ? value : null);

// Two readers of the contract (the switches and the limits) mount together
// and share one request: only callers in the same tick do, so a later call
// always asks again.
let sameTick = null;
function readContract() {
	if (!sameTick) {
		sameTick = api.get(`${API_URL}/docs/contract`, { timeout: 8000 });
		Promise.resolve().then(() => { sameTick = null; });
	}
	return sameTick;
}

export async function getServerFeatures() {
	try {
		const response = await readContract();
		const features = response?.data?.features;
		if (!features || typeof features !== 'object') return NO_FEATURES;
		return { repeatingGroups: asFlag(features.repeatingGroups) };
	} catch (error) {
		return NO_FEATURES;
	}
}

/**
 * The contract's numeric limits the editor shows, from the same public call:
 * `limits.relatedListsPerCard` (keepr-api KPR-716) is how many related lists a
 * card may define. `null` when the contract does not say (an API from before
 * it, or no answer): the caller then uses its own default and lets the API's
 * 400 `too_many_related_lists` have the last word.
 */
export const NO_LIMITS = Object.freeze({ relatedListsPerCard: null });

const asCount = value => (Number.isInteger(value) && value > 0 ? value : null);

export async function getServerLimits() {
	try {
		const response = await readContract();
		const limits = response?.data?.limits;
		if (!limits || typeof limits !== 'object') return NO_LIMITS;
		return { relatedListsPerCard: asCount(limits.relatedListsPerCard) };
	} catch (error) {
		return NO_LIMITS;
	}
}

export default getServerFeatures;
